/**
 * Light / dark theme — one choice per browser, applied before first paint.
 *
 * WHAT. Light is the default and renders exactly as the dashboard always has.
 * Dark is `<html data-theme="dark">`, which switches every `--c-*` / `--k-*`
 * colour token in `styles.css` (section 0) to the useclavis.ai dark palette.
 * The choice lives in `localStorage["layer.theme"]` (`"light" | "dark"`), so
 * it is per browser and per origin, and it outlives sign-out on purpose — it
 * is a display preference, not session state.
 *
 * WHERE IT IS READ. Twice, deliberately:
 *   1. `web/index.html` carries a tiny inline script that reads the key and sets
 *      the attribute while `<head>` is still parsing, i.e. before the first
 *      paint, so a dark reload never flashes light. It cannot import this
 *      module (it must run before the bundle), so it restates the key; the
 *      test beside this file pins that the two agree.
 *   2. `createThemeStore` (one per page, used by every `<ThemeToggle>`) reads it
 *      again, then owns every change: the attribute, the stored value, and
 *      telling each mounted toggle.
 *
 * Every storage access is guarded: `localStorage` throws in some contexts
 * (blocked site data, some private windows). A failed read means "light"; a
 * failed write means the choice lasts for this page only. Neither is an error
 * worth surfacing — the page must render either way.
 */

import type { StorageLike } from "./saved-logins";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "layer.theme";

/** The stored choice; anything absent, unreadable or unknown is `light`. */
export function readTheme(storage: StorageLike | null): Theme {
  if (storage === null) return "light";
  try {
    return storage.getItem(THEME_STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** Remember the choice; a storage that refuses leaves it page-local. */
export function writeTheme(storage: StorageLike | null, theme: Theme): void {
  if (storage === null) return;
  try {
    storage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Blocked storage: the attribute is still applied, just not remembered.
  }
}

/** A `storage` event's new value, read the same way as `readTheme`. */
export function themeFromStoredValue(value: string | null): Theme {
  return value === "dark" ? "dark" : "light";
}

/**
 * Light is the ABSENCE of the attribute, never `data-theme="light"`, so the
 * default path and a switched-back-to-light page are the same DOM.
 */
export function applyTheme(root: Pick<Element, "setAttribute" | "removeAttribute">, theme: Theme): void {
  if (theme === "dark") root.setAttribute("data-theme", "dark");
  else root.removeAttribute("data-theme");
}

/** The theme the page is showing: the attribute is the one source of truth. */
export function themeOf(root: Pick<Element, "getAttribute">): Theme {
  return root.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

type ThemeRoot = Pick<Element, "getAttribute" | "setAttribute" | "removeAttribute">;
interface StorageEventTarget {
  addEventListener(type: "storage", listener: (e: { key: string | null; newValue: string | null }) => void): void;
  removeEventListener(type: "storage", listener: (e: { key: string | null; newValue: string | null }) => void): void;
}

export interface ThemeStore {
  /** The current theme (for `useSyncExternalStore`'s snapshot). */
  get(): Theme;
  /** Apply, remember and announce a choice. */
  set(theme: Theme): void;
  /** Be told of every change — this tab's toggles and other tabs alike. */
  subscribe(listener: () => void): () => void;
}

/**
 * The page's ONE theme state, shared by every toggle on it. There can be two
 * (the desktop top bar's, and the one in the phone's More sheet, beside the
 * studio's logo), and a
 * `storage` event never reaches the tab that wrote the value — so without a
 * shared store a click on one leaves the other showing the old state.
 *
 * Creating the store reconciles the attribute with storage once: normally the
 * `index.html` pre-paint script already did, but if that script could not run
 * (a future CSP without its hash) the page still ends up in the stored theme.
 */
export function createThemeStore(root: ThemeRoot, storage: StorageLike | null, events: StorageEventTarget): ThemeStore {
  applyTheme(root, readTheme(storage));
  const listeners = new Set<() => void>();
  const announce = (): void => {
    for (const l of listeners) l();
  };
  const onStorage = (e: { key: string | null; newValue: string | null }): void => {
    if (e.key !== THEME_STORAGE_KEY) return;
    applyTheme(root, themeFromStoredValue(e.newValue));
    announce();
  };
  return {
    get: () => themeOf(root),
    set: (theme) => {
      writeTheme(storage, theme);
      applyTheme(root, theme);
      announce();
    },
    subscribe: (listener) => {
      if (listeners.size === 0) events.addEventListener("storage", onStorage);
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
        if (listeners.size === 0) events.removeEventListener("storage", onStorage);
      };
    },
  };
}
